import { Button, Card, Flex, Form, Input, InputNumber, Typography } from 'antd';
import { type ReactNode, useEffect, useMemo } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import type Supply from '../../../../entities/supply';
import { useNotification } from '../../../../hooks/use-notification';
import { useMutation } from '../../../../jsonapi-provider';

const SUCCESS_ADD_MESSAGE = 'Supply added successfully';
const SUCCESS_UPDATE_MESSAGE = 'Supply updated successfully';

interface SupplyFormProps {
  supply: Supply;
  onSuccess: () => void;
  onCancel: () => void;
}

const SupplyForm = (props: SupplyFormProps): ReactNode => {
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { supply, onSuccess, onCancel } = props;
  const isEditMode = useMemo(() => !!supply.id, [supply.id]);
  const initialValues = supply;

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const [mutateSupply] = useMutation<Supply>(supply?.id ? ['supply', supply.id] : ['supply']);
  const onSubmit = (): void => {
    from(formInstance.validateFields())
      .pipe(
        switchMap((values) =>
          from(
            mutateSupply({
              id: supply?.id,
              ...values,
            })
          ).pipe(
            switchMap((res) =>
              res.error ? throwError(() => new Error(res.error?.title)) : of(res.data)
            ),
            tap(() => {
              notification.success({
                description: !isEditMode ? SUCCESS_ADD_MESSAGE : SUCCESS_UPDATE_MESSAGE,
                message: '',
              });
              onSuccess();
              formInstance.resetFields();
            }),
            catchError((error) => {
              const [description, title, ..._err] = error?.message.split('\n');
              notification.error({
                description: description ?? 'Failed to save the supply',
                message: title ?? '',
              });
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  };
  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="supply-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>{isEditMode ? supply.name : 'New Supply'}</Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input />
          </Form.Item>

          <Form.Item label="Price" name="price" rules={[{ required: true }]}>
            <InputNumber addonBefore="$" min={0} precision={2} />
          </Form.Item>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button block htmlType="submit" type="primary">
                Save
              </Button>
              <Button block onClick={onCancel} disabled={false}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default SupplyForm;
